import { memo, useState } from "react";
import { Box, Modal, IconButton } from "@mui/material";

import Close from "@zenith/assets/icons/Close";
import ChevronDown from "@zenith/assets/icons/ChevronDown";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";
import ChevronUp from "@zenith/assets/icons/ChevronUp";
import { ButtonComponent } from "@zenith/components";
import type { BusinessType } from "@zenith/features/AuthZ/permissions";
import type { TeamRoleGuideDTO } from "@zenith/services/user/services/TeamManagementControllerService";
import {
  getPermissionCategories,
  ROLE_DEFINITIONS,
  TeamMemberRole,
} from "../../data/roles";

export interface RolePermissionModalProps {
  showModal: boolean;
  role: TeamMemberRole | null;
  businessType: BusinessType;
  /** GET /members/{userId}'s own accessGuide, for a specific member being
   * viewed - that member's role, not a hypothetical one or the signed-in
   * viewer's. Falls back to the static table when absent (add-flow role
   * preview, or an edit-flow role not yet saved). */
  liveAccessGuide?: TeamRoleGuideDTO;
  onClose: () => void;
  onBack?: () => void;
}

const RolePermissionModal = memo(function RolePermissionModal(
  props: RolePermissionModalProps
) {
  const { showModal, role, businessType, liveAccessGuide, onClose, onBack } =
    props;
  const [expandedCategory, setExpandedCategory] = useState<string | null>(
    "Home"
  );

  if (!role) return null;

  const roleLabel = ROLE_DEFINITIONS.find((r) => r.id === role)?.label ?? "";
  const categories = liveAccessGuide?.sections?.length
    ? liveAccessGuide.sections.map((section) => ({
        name: section.name ?? "",
        privileges: section.actions ?? [],
      }))
    : getPermissionCategories(role, businessType);

  return (
    <Modal open={showModal} onClose={onClose} className="overflow-visible">
      <Box className="absolute left-1/2 top-1/2 z-0 flex max-h-[90vh] w-[95%] max-w-[664px] -translate-x-1/2 -translate-y-1/2 transform flex-col overflow-y-auto rounded-xl bg-white p-8 outline-none">
        <div className="mb-4 flex items-center justify-between">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-1 text-sm text-neutral-600"
            >
              <ChevronLeft />
              Back
            </button>
          ) : (
            <span />
          )}
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </div>

        <h2 className="mb-4 text-2xl font-semibold text-neutral-900">
          {roleLabel} Permission
        </h2>
        <p className="mb-6 text-sm text-neutral-600">
          Adding an authorized user with an {roleLabel} access allows you to
          grant them limited access to your accounts or financial products with
          the following privileges:
        </p>

        <div className="flex flex-col">
          {categories.map((category) => {
            const isOpen = expandedCategory === category.name;
            return (
              <div
                key={category.name}
                className={`border-b border-[#DDDDDD] last:border-b-0 ${
                  isOpen ? "bg-[#F9F9F9]" : ""
                }`}
              >
                <button
                  type="button"
                  onClick={() =>
                    setExpandedCategory(isOpen ? null : category.name)
                  }
                  className="flex min-h-[84px] w-full items-center justify-between px-4 text-left text-sm font-medium text-neutral-900"
                >
                  {category.name}
                  {isOpen ? <ChevronUp /> : <ChevronDown />}
                </button>
                {isOpen && (
                  <div className="border-t border-[#DDDDDD] px-4 py-3">
                    {category.privileges.length > 0 ? (
                      <ul className="list-disc space-y-1 pl-4 text-xs text-neutral-500">
                        {category.privileges.map((privilege) => (
                          <li key={privilege}>{privilege}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-neutral-400">
                        Permission details coming soon.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6">
          <ButtonComponent
            id="role-permission-go-back"
            type="secondary"
            size="large"
            label="Go Back"
            className="w-full"
            onClick={onBack ?? onClose}
          />
        </div>
      </Box>
    </Modal>
  );
});

export default RolePermissionModal;
